import {useCallback, useMemo} from 'react';
import type {RunSetPart} from 'src/state/views/runSet/types';

import * as CustomRunColorsActions from '../../../state/views/customRunColors/actions';
import * as ViewHooks from '../../../state/views/hooks';
import {usePartMaybe} from '../../../state/views/hooks';
import {type Ref as RunSetRef} from '../../../state/views/runSet/types';
import {runColor} from '../../../util/colors';
import {activeFilterCountFromStored} from '../../../util/filterV2';
import type * as QueryTypes from '../../../util/queryTypes';
import {type Sort} from '../../../util/queryTypes';
import {
  getParsedRunColorGroupKeys,
  getRunColorGroupDescendantKeysFromParsedKeys,
  getRunColorGroupKeyForDepth,
  type ParsedRunColorGroupKeyWithSource,
} from '../../../util/runColorGroup';
import {type RunWithUniqueId, uniqueId} from '../../../util/runs';
import {useMaybePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';

export const useGroupKeysByRunsetId = (runSets: RunSetPart[]) => {
  const groupSelections = ViewHooks.useParts(
    runSets.map(runSet => runSet.groupSelectionsRef)
  );

  const groupKeysByRunSetId = useMemo(() => {
    return runSets.reduce(
      (acc, runSet, index) => {
        acc[runSet.id] = groupSelections[index].grouping;
        return acc;
      },
      {} as Partial<Record<string, QueryTypes.Grouping>>
    );
  }, [runSets, groupSelections]);

  const getGroupKeysByRunsetId = useCallback(
    (runsetId: string | undefined) => {
      return runsetId ? (groupKeysByRunSetId[runsetId] ?? []) : [];
    },
    [groupKeysByRunSetId]
  );

  return getGroupKeysByRunsetId;
};

export const useDetectActiveSearch = (runSets: RunSetPart[]) => {
  const getActiveSearchByRunsetId = useCallback(
    (runsetId: string | undefined) => {
      return runSets.find(runSet => runSet.id === runsetId)?.search.query;
    },
    [runSets]
  );

  return getActiveSearchByRunsetId;
};

export const useDetectActiveFilters = (runSets: RunSetPart[]) => {
  const filters = ViewHooks.useParts(runSets.map(runSet => runSet.filtersRef));

  const activeFiltersByRunsetId = useMemo(() => {
    return runSets.reduce(
      (acc, runSet, index) => {
        acc[runSet.id] = activeFilterCountFromStored(filters[index]) > 0;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [runSets, filters]);

  const getActiveFiltersByRunsetId = useCallback(
    (runsetId: string) => {
      return activeFiltersByRunsetId[runsetId];
    },
    [activeFiltersByRunsetId]
  );

  const hasActiveFilter = useMemo(() => {
    return runSets.some(runSet => getActiveFiltersByRunsetId(runSet.id));
  }, [runSets, getActiveFiltersByRunsetId]);

  return hasActiveFilter;
};

const EMPTY_RUNS_ARRAY: ResetColorRun[] = [];

/**
 * For each visible grouped row, get its run color group key and any descendant
 * run color group keys that currently have custom colors.
 */
function getVisibleRunColorGroupKeys(
  customRunColors: Record<string, string> | undefined,
  rows: ResetColorRun[],
  getGroupKeysByRunsetId: (runsetId: string | undefined) => QueryTypes.Grouping,
  parsedRunColorGroupKeys: ParsedRunColorGroupKeyWithSource[]
) {
  if (customRunColors == null) {
    return [];
  }

  const keys = rows.flatMap(row => {
    const groupKeys = getGroupKeysByRunsetId(row.runsetInfo?.id);
    if (groupKeys.length === 0 || row.groupCounts == null) {
      return [];
    }

    const key = getRunColorGroupKeyForDepth(row, groupKeys, 0);
    return [
      ...(key in customRunColors ? [key] : []),
      ...getRunColorGroupDescendantKeysFromParsedKeys(
        parsedRunColorGroupKeys,
        key
      ),
    ];
  });
  return Array.from(new Set(keys));
}

function getVisibleRowRunColorKeys(
  row: ResetColorRun,
  groupKeys: QueryTypes.Grouping
) {
  const keys = [uniqueId(row, groupKeys)];

  if (groupKeys.length > 0 && row.groupCounts != null) {
    keys.push(uniqueId(row, groupKeys.slice(0, 1)));
  }

  return Array.from(new Set(keys));
}

export function useResetColorsInfo(
  sort: Sort,
  runSelectorRows: ResetColorRun[],
  // Used to get around rules of hooks to avoid calculating this stuff if the menu isn't even open
  skip: boolean
) {
  const sharedRefsContext = useMaybePanelsSharedRefsContext();
  const rows = skip ? EMPTY_RUNS_ARRAY : runSelectorRows;
  const runSetRefs = skip
    ? EMPTY_RUNSET_REFS_ARRAY
    : (sharedRefsContext?.runSetRefs ?? EMPTY_RUNSET_REFS_ARRAY);
  const customRunColorsRef = skip
    ? null
    : sharedRefsContext?.customRunColorsRef;

  const runSets = ViewHooks.useParts(runSetRefs);
  const hasActiveFilter = useDetectActiveFilters(runSets);
  const getActiveSearchByRunsetId = useDetectActiveSearch(runSets);
  const hasActiveSearch = rows.some(r =>
    getActiveSearchByRunsetId(r.runsetInfo?.id)
  );

  const getGroupKeysByRunsetId = useGroupKeysByRunsetId(runSets);
  const customRunColors = usePartMaybe(customRunColorsRef);

  const parsedRunColorGroupKeys = useMemo(
    () => getParsedRunColorGroupKeys(customRunColors),
    [customRunColors]
  );
  const rowRunColorKeys = useMemo(
    () =>
      rows.flatMap(r =>
        getVisibleRowRunColorKeys(r, getGroupKeysByRunsetId(r.runsetInfo?.id))
      ),
    [getGroupKeysByRunsetId, rows]
  );
  const runColorGroupKeys = useMemo(
    () =>
      getVisibleRunColorGroupKeys(
        customRunColors,
        rows,
        getGroupKeysByRunsetId,
        parsedRunColorGroupKeys
      ),
    [customRunColors, getGroupKeysByRunsetId, parsedRunColorGroupKeys, rows]
  );
  const hasActiveGroupKeys = rows.some(
    r => getGroupKeysByRunsetId(r.runsetInfo?.id).length > 0
  );
  const hasCustomRunColors =
    customRunColors &&
    (rowRunColorKeys.some(r => r in customRunColors) ||
      runColorGroupKeys.length > 0);
  return {
    hasActiveGroupKeys,
    hasActiveFilter,
    hasActiveSearch,
    hasCustomRunColors,
    modifiedSort:
      sort.keys.length !== 1 || sort.keys[0].key.name !== 'createdAt',
    runColorGroupKeys,
  };
}

// This is a workaround to avoid creating a new array on every render
const EMPTY_RUNSET_REFS_ARRAY: RunSetRef[] = [];

/**
 * The way this works is that we're hijacking the custom run colors functionality to randomize the colors of runs after a sort. Runs are typically colored by their `defaultColorIndex` property, and we can easily override this by selectively overriding that value when calculating the color. We then store that color in the custom run colors slot in the view spec, so that it persists across user interactions and can easily be copied into reports.
 *
 * Resetting is a simple matter of nulling out those values in the custom run colors slot. This won't null out all custom run colors— only run colors that are in view through the selector (exposed to this hook as `rows`).
 *
 * Grouped parent rows persist random colors with run color group keys so parent
 * colors can inherit through expanded children. We still clear exact run color
 * keys for compatibility with colors saved before group keys existed.
 */

export type ResetColorRun = RunWithUniqueId & {
  groupCounts?: number[] | null | undefined;
  id: string;
};
export function useResetColors(sort: Sort, rows: ResetColorRun[]) {
  const {
    hasActiveGroupKeys,
    hasActiveFilter,
    hasActiveSearch,
    hasCustomRunColors,
    modifiedSort,
    runColorGroupKeys,
  } = useResetColorsInfo(sort, rows, false);
  const sharedRefsContext = useMaybePanelsSharedRefsContext();
  const runSetRefs = sharedRefsContext?.runSetRefs ?? EMPTY_RUNSET_REFS_ARRAY;
  const customRunColorsRef = sharedRefsContext?.customRunColorsRef;

  const runSets = ViewHooks.useParts(runSetRefs);

  const getGroupKeysByRunsetId = useGroupKeysByRunsetId(runSets);
  const setCustomRunColors = ViewHooks.useMaybeViewAction(
    customRunColorsRef,
    CustomRunColorsActions.setCustomRunColors
  );
  const customRunColors = usePartMaybe(customRunColorsRef);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const randomizeColors = useCallback(() => {
    const colorUpdates: Record<string, string | null> = {};
    rows.forEach((r, i) => {
      const groupKeys = getGroupKeysByRunsetId(r.runsetInfo?.id);
      const color = runColor({
        run: {...r, defaultColorIndex: i},
        groupKeys,
        customRunColors: undefined,
        useGroupDefaultIndex: true,
        colorPaletteOption,
      });

      if (groupKeys.length > 0 && r.groupCounts != null) {
        const groupColorKey = getRunColorGroupKeyForDepth(r, groupKeys, 0);
        colorUpdates[groupColorKey] = color;
        getVisibleRowRunColorKeys(r, groupKeys).forEach(key => {
          colorUpdates[key] = null;
        });
      } else {
        colorUpdates[uniqueId(r, groupKeys)] = color;
      }
    });
    runColorGroupKeys.forEach(key => {
      if (!(key in colorUpdates)) {
        colorUpdates[key] = null;
      }
    });

    if (Object.keys(colorUpdates).length > 0) {
      setCustomRunColors(colorUpdates);
    }
  }, [
    getGroupKeysByRunsetId,
    rows,
    runColorGroupKeys,
    setCustomRunColors,
    colorPaletteOption,
  ]);

  const resetColors = useCallback(() => {
    const colorUpdates: Record<string, null> = {};
    rows.forEach(r => {
      const groupKeys = getGroupKeysByRunsetId(r.runsetInfo?.id);
      getVisibleRowRunColorKeys(r, groupKeys).forEach(key => {
        if (customRunColors && key in customRunColors) {
          colorUpdates[key] = null;
        }
      });
    });
    runColorGroupKeys.forEach(key => {
      colorUpdates[key] = null;
    });

    if (Object.keys(colorUpdates).length > 0) {
      setCustomRunColors(colorUpdates);
    }
  }, [
    getGroupKeysByRunsetId,
    rows,
    runColorGroupKeys,
    setCustomRunColors,
    customRunColors,
  ]);

  return useMemo(() => {
    return {
      hasActiveGroupKeys,
      hasActiveFilter,
      hasActiveSearch,
      hasCustomRunColors,
      modifiedSort,
      randomizeColors,
      resetColors,
    };
  }, [
    hasActiveGroupKeys,
    hasActiveFilter,
    hasActiveSearch,
    hasCustomRunColors,
    modifiedSort,
    randomizeColors,
    resetColors,
  ]);
}
